import React, { type JSX } from 'react';

import { CustomModal, CustomTransComponent } from 'components';
import { Box, InputAdornment, List, Stack, Switch, TextField } from '@mui/material';
import { ChannelValue, InfoListItem } from '@brightlayer-ui/react-components';
import { NorthEast, SouthWest } from '@mui/icons-material';
import { CustomSlider } from './';
import { ModalActions } from 'components/CustomModal';
import { useConfirm } from '@fiji/common/src/hooks';
import { useHandleDeviceDetails } from './hooks';
import { useGetThresholdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useParams } from 'react-router-dom';
import { useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useTypedSelector } from '@fiji/common';
import { t } from 'i18next';

export const thresholdName: any = {
    lowCritical: t('THRESHOLD_CONFIGURATION:LOW_CRITICAL'),
    lowWarning: t('THRESHOLD_CONFIGURATION:LOW_WARNING'),
    highWarning: t('THRESHOLD_CONFIGURATION:HIGH_WARNING'),
    highCritical: t('THRESHOLD_CONFIGURATION:HIGH_CRITICAL'),
};

interface ThresholdModalProps {
    mode?: 'view' | 'edit';
    subHeader?: React.ReactNode;
    warningBanner?: React.ReactNode;
}

export const ThresholdModal = React.memo(
    React.forwardRef<any, ThresholdModalProps>(({ mode, subHeader, warningBanner }, ref): JSX.Element => {
        const { deviceId } = useParams();
        const { data, isVisible, onCancel, onClick, onConfirm } = useConfirm((args: any): any => {
            submitHandler(args, onCancel) as any;
        });

        const permissions = useTypedSelector(selectCurrentPermission);

        const { hasPermission } = useRBAC(permissions);
        const canUpdateThreshold = hasPermission('edit-devices');
        const isViewMode = mode === 'view' || (mode === undefined && !canUpdateThreshold);

        const [isChangedByInput, setIsChangedByInput] = React.useState(true);

        const { payload, setPayload, resetPayload, submitHandler, thresholdUpdateLoader } = useHandleDeviceDetails();

        const {
            currentData: thresholdData,
            isLoading,
            isFetching,
            isError,
        } = useGetThresholdQuery(
            { channelKey: data?.channelId, deviceId: deviceId ?? data?.deviceId },
            { skip: !(deviceId || data?.deviceId) || !data?.channelId, refetchOnMountOrArgChange: true }
        );

        React.useImperativeHandle(
            ref,
            () => ({
                onClick,
            }),
            []
        );

        React.useEffect(() => {
            if (thresholdData?.data?.threshold?.length && isVisible) {
                const newPayload = JSON.parse(JSON.stringify(payload ?? []));
                setPayload(
                    newPayload?.map((item: any) => ({
                        ...item,
                        value: thresholdData.data.threshold?.find((ele: any) => ele?.name === item?.id)?.value,
                        isEnabled:
                            thresholdData.data.threshold?.find((ele: any) => ele?.name === item?.id)?.isEnabled ??
                            false,
                    }))
                );
            }
        }, [thresholdData, isVisible]);

        const getIsDisabled = (): boolean =>
            !payload?.some((item: any) => {
                const oldData: any = thresholdData?.data?.threshold?.find((ele: any) => ele?.name === item?.id);
                return oldData?.value !== item.value || oldData?.isEnabled !== item?.isEnabled;
            });
        const getSubtitle = (element: any): string => {
            const enabledPayload = payload?.filter((item: any) => item?.isEnabled) ?? [];
            const index = enabledPayload.findIndex((item: any) => item?.id === element?.id);
            const prev = index > 0 ? enabledPayload[index - 1] : undefined;
            const next = index > -1 && index < enabledPayload.length - 1 ? enabledPayload[index + 1] : undefined;

            if (prev?.value >= element?.value) {
                return `${t('COMMON:MUST_BE_GREATER_THAN') as any} ${prev?.name}`;
            }
            if (next?.value <= element?.value) {
                return `${t('COMMON:MUST_BE_LESS_THAN') as any} ${next?.name}`;
            }
            return '';
        };
        const disableSave = (): boolean =>
            !payload || payload?.filter((item: any) => item?.isEnabled)?.some((ele: any) => getSubtitle(ele)?.length);

        const actions: ModalActions[] = [
            {
                key: 'cancel',
                label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
                close: true,
                variant: 'outlined',
                handleClick: (): void => {
                    onCancel(true);
                    resetPayload();
                },
            },
            {
                key: 'save',
                label: thresholdUpdateLoader ? t('COMMON:SAVING') : t('COMMON:SAVE_LABEL'),
                variant: 'contained',
                isLoading: thresholdUpdateLoader,
                disabled: getIsDisabled() || isError || disableSave(),
                handleClick: onConfirm,
            },
        ];

        const handleThresholdChange = (e: any): void => {
            if (!isNaN(e.target?.value) && +e.target?.value <= (thresholdData?.data?.max || data?.value || 10)) {
                const { id, value } = e.target;
                const updatedPayload = payload.map((item: any) =>
                    item?.id === id ? { ...item, value: +value } : item
                );
                setPayload(updatedPayload);
                setIsChangedByInput(true);
            }
        };

        const toggleEnabledHandler = (eleName: string, isEnabled: boolean): void => {
            const updatedPayload = payload.map((item: any) =>
                item?.id === eleName ? { ...item, isEnabled: isEnabled } : item
            );
            setPayload(updatedPayload);
            setIsChangedByInput(true);
        };

        const getIcon = (eleName: string): JSX.Element => {
            const iconMap: Record<string, JSX.Element> = {
                lowCritical: <SouthWest color="error" />,
                lowWarning: <SouthWest color="warning" />,
                highWarning: <NorthEast color="warning" />,
                highCritical: <NorthEast color="error" />,
            };
            return iconMap[eleName] ?? <></>;
        };

        return (
            <CustomModal
                isOpen={isVisible}
                isLoading={isLoading || isFetching}
                type="medium"
                header={`${isViewMode ? t('COMMON:VIEW') : t('COMMON:EDIT')} ${data?.displayName ?? '-'} Thresholds`}
                actions={
                    isViewMode
                        ? ([
                              {
                                  key: 'cancel',
                                  label: t('bluiRegistration:ACTIONS.OKAY'),
                                  close: true,
                                  variant: 'contained',
                                  handleClick: onCancel,
                              },
                          ] as any)
                        : actions
                }
                hidePadding
                noDividers
                actionsDivider
                subHeader={
                    subHeader ?? <CustomTransComponent translationKey={'DEVICE_DETAILS:SET_THRESHOLDS_VALUES'} />
                }
                footerClass={isViewMode ? '' : 'space-between-footer'}
            >
                <Stack py={2} px={3} m={2}>
                    {warningBanner}
                    <List disablePadding>
                        {payload?.map(
                            (ele: any): JSX.Element => (
                                <InfoListItem
                                    key={ele?.id}
                                    sx={{
                                        '& .BluiInfoListItem-subtitle': {
                                            color: 'red',
                                        },
                                    }}
                                    disableGutters
                                    icon={getIcon(ele?.id)}
                                    title={ele?.name}
                                    subtitle={ele?.isEnabled ? getSubtitle(ele) : ''}
                                    rightComponent={
                                        <Box
                                            maxWidth={'200px'}
                                            display={'flex'}
                                            flexDirection={'row'}
                                            alignItems={'center'}
                                        >
                                            {!isViewMode ? (
                                                <>
                                                    <TextField
                                                        variant="filled"
                                                        value={ele?.value}
                                                        onChange={handleThresholdChange}
                                                        id={ele?.id}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="start">
                                                                    {data?.unit}
                                                                </InputAdornment>
                                                            ),
                                                            readOnly: !ele.isEnabled,
                                                        }}
                                                    />
                                                    <Switch
                                                        checked={ele?.isEnabled}
                                                        onChange={() => toggleEnabledHandler(ele?.id, !ele?.isEnabled)}
                                                        inputProps={{ 'aria-label': 'controlled' }}
                                                    />
                                                </>
                                            ) : (
                                                <ChannelValue value={ele?.value ?? 0} units={data?.unit} />
                                            )}
                                        </Box>
                                    }
                                />
                            )
                        )}
                    </List>
                    <CustomSlider
                        isDisabled={isViewMode}
                        payload={payload}
                        setPayload={setPayload}
                        min={thresholdData?.data?.min || 0}
                        max={thresholdData?.data?.max || data?.value}
                        unit={data?.unit}
                        thresholdData={thresholdData}
                        isVisible={isVisible}
                        isChangedByInput={isChangedByInput}
                        setIsChangedByInput={setIsChangedByInput}
                    />
                </Stack>
            </CustomModal>
        );
    })
);
